import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Input, Button, Row, Col, Typography, message, InputNumber, Statistic, Divider, Radio, Space, Tag, Select, Tooltip, Pagination, Badge } from 'antd';
import { ShoppingCartOutlined, SearchOutlined, EuroOutlined, CheckCircleOutlined, WalletOutlined, PrinterOutlined, DeleteOutlined, FilterOutlined, InfoCircleOutlined, FullscreenOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import ProductCategorySelect from '../components/ProductCategorySelect';
import CategoryTag from '../components/CategoryTag';
import ProductGridCard from './POS/ProductGridCard';
import CartDrawer from './POS/CartDrawer';
import InvoicePrintModal from '../components/InvoicePrintModal';
import useProductStore from '../store/useProductStore';
import { AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import EmptyState from '../components/EmptyState';

const { Title, Text } = Typography;
const { Option } = Select;

const POS = () => {
    // State
    const { products, loading: storeLoading, fetchProducts, optimisticDecrement } = useProductStore();
    const [categories, setCategories] = useState([]);
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(false);

    // Checkout State
    const [checkoutMode, setCheckoutMode] = useState(false); // New: Toggle between Cart & Checkout inside Drawer
    const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [branding, setBranding] = useState({});
    const [paymentAmount, setPaymentAmount] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState(4); // 4=Cash, 6=CliQ

    // Filters & View Mode
    const [searchText, setSearchText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(localStorage.getItem('pos_selected_category') || 'all');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [viewMode, setViewMode] = useState(localStorage.getItem('pos_view_mode') || 'list');
    const [gridDensity, setGridDensity] = useState(parseInt(localStorage.getItem('pos_grid_density')) || 2);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(12);

    // State for Cashier Mode & Drawer
    const [cashierMode, setCashierMode] = useState(localStorage.getItem('pos_cashier_mode') === 'true');
    // Persistence G: Cart drawer state
    const [cartDrawerVisible, setCartDrawerVisible] = useState(localStorage.getItem('pos_cart_visible') === 'true');

    // Adjustments State
    const [adjustments, setAdjustments] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('pos_adjustments')) || [];
        } catch { return []; }
    });

    // Auto-Print State
    const [printVisible, setPrintVisible] = useState(false);
    const [autoPrint, setAutoPrint] = useState(false);

    // Persistence
    useEffect(() => {
        localStorage.setItem('pos_cashier_mode', cashierMode);
        if (cashierMode) {
            document.body.classList.add('cashier-mode');
        } else {
            document.body.classList.remove('cashier-mode');
        }
        return () => document.body.classList.remove('cashier-mode');
    }, [cashierMode]);

    useEffect(() => {
        localStorage.setItem('pos_cart_visible', cartDrawerVisible);
    }, [cartDrawerVisible]);

    useEffect(() => {
        localStorage.setItem('pos_adjustments', JSON.stringify(adjustments));
    }, [adjustments]);

    useEffect(() => {
        fetchProducts();
        axios.get('/api/branding').then(res => setBranding(res.data)).catch(() => { });
    }, []);

    // Search Debounce
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
        return () => clearTimeout(timer);
    }, [searchText]);

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            const isInput = ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName);

            // ESC key logic (Rule F)
            if (e.key === 'Escape') {
                if (cartDrawerVisible) {
                    // If in checkout mode, back to cart mode? Or just close?
                    // "Closes cart if open"
                    setCartDrawerVisible(false);
                    setCheckoutMode(false); // Reset checkout mode
                } else if (cashierMode) {
                    setCashierMode(false);
                    if (document.fullscreenElement) document.exitFullscreen();
                }
                return;
            }

            // Focus Search
            if (e.key === '/' && !isInput) {
                e.preventDefault();
                document.getElementById('pos-search-input')?.focus();
            }
            // Toggle View Mode
            if (e.key.toLowerCase() === 'v' && !isInput) {
                e.preventDefault();
                const newMode = viewMode === 'list' ? 'grid' : 'list';
                setViewMode(newMode);
                localStorage.setItem('pos_view_mode', newMode);
                message.info(`Switched to ${newMode} view`, 1);
            }
            // Toggle Cart Drawer
            if (e.key.toLowerCase() === 'c' && !isInput) {
                e.preventDefault();
                setCartDrawerVisible(prev => !prev);
            }
            // Toggle Cashier Mode (Fullscreen)
            if (e.key.toLowerCase() === 'f' && !isInput) {
                e.preventDefault();
                toggleCashierMode();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [viewMode, cashierMode, cartDrawerVisible]);

    const toggleCashierMode = () => {
        const newMode = !cashierMode;
        setCashierMode(newMode);

        if (newMode) {
            document.documentElement.requestFullscreen().catch((err) => {
                console.error("Error attempting to enable fullscreen:", err);
            });
        } else {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            }
        }
    };

    // Exit fullscreen listener to sync state if user presses ESC
    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement && cashierMode) {
                setCashierMode(false);
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, [cashierMode]);


    // LocalStorage for Category
    useEffect(() => {
        localStorage.setItem('pos_selected_category', selectedCategory);
    }, [selectedCategory]);

    // Filtering Logic
    const filteredProducts = useMemo(() => {
        if (!Array.isArray(products)) return [];
        const search = (debouncedSearch || '').toLowerCase().trim();
        return products.filter(p => {
            if (!p) return false;
            const matchesSearch = !search ||
                (p.label || '').toLowerCase().includes(search) ||
                (p.ref || '').toLowerCase().includes(search) ||
                (p.barcode || '').toLowerCase().includes(search);

            const matchesCategory =
                selectedCategory === 'all' ? true :
                    selectedCategory === 'uncategorized' ? !p.category_id :
                        p.category_id == selectedCategory;

            return matchesSearch && matchesCategory;
        });
    }, [products, debouncedSearch, selectedCategory]);

    // Cart Logic
    const addToCart = (product) => {
        if (product.stock < 1 && product.type === 0) {
            message.warning('Product out of stock');
            return;
        }
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                if (existing.qty + 1 > product.stock && product.type === 0) {
                    message.warning(`Cannot add more. Only ${product.stock} in stock.`);
                    return prev;
                }

                const updatedItem = { ...existing, qty: existing.qty + 1 };
                if (product.is_used && product.available_ids) {
                    // Take the next available ID
                    const nextId = product.available_ids[existing.qty];
                    if (nextId) {
                        updatedItem.purchase_item_ids = [...(existing.purchase_item_ids || []), nextId];
                    }
                }
                return prev.map(item => item.id === product.id ? updatedItem : item);
            }

            const newItem = { ...product, qty: 1 };
            if (product.is_used && product.available_ids && product.available_ids.length > 0) {
                newItem.purchase_item_ids = [product.available_ids[0]];
            }
            return [...prev, newItem];
        });
    };

    const updateQty = (id, qty) => {
        const cartItem = cart.find(item => item.id === id);
        if (!cartItem) return;

        if (qty > cartItem.stock && cartItem.type === 0) {
            message.warning(`Only ${cartItem.stock} in stock.`);
            return;
        }

        const newQty = Math.max(1, qty);
        setCart(prev => prev.map(item => {
            if (item.id === id) {
                const updated = { ...item, qty: newQty };
                if (item.is_used && item.available_ids) {
                    updated.purchase_item_ids = item.available_ids.slice(0, newQty);
                }
                return updated;
            }
            return item;
        }));
    };

    const removeItem = (id) => {
        setCart(prev => prev.filter(item => item.id !== id));
    };

    const addAdjustment = (adj) => {
        setAdjustments(prev => [...prev, adj]);
    };

    const removeAdjustment = (idx) => {
        setAdjustments(prev => prev.filter((_, i) => i !== idx));
    };

    // Calculations
    const safeCart = Array.isArray(cart) ? cart : [];
    const safeAdjustments = Array.isArray(adjustments) ? adjustments : [];

    const cartTTC = safeCart.reduce((sum, item) => sum + ((item?.price || 0) * (item?.qty || 0)), 0);
    const discountTotal = safeAdjustments.filter(a => a?.kind === 'DISCOUNT').reduce((sum, a) => sum + (a?.amount || 0), 0);
    const feeTotal = safeAdjustments.filter(a => a?.kind === 'FEE').reduce((sum, a) => sum + (a?.amount || 0), 0);
    const totalTTC = Math.max(0, cartTTC - discountTotal + feeTotal);

    // Checkout Flow Logic (Drawer Based)
    const handleCheckout = async () => {
        if (cart.length === 0) return message.warning('Cart is empty');

        // Skip "Create" Step UI - Do it in background then switch Drawer to Payment
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const items = cart.map(item => ({
                id: item.id,
                qty: item.qty,
                price: item.price,
                vat_rate: item.vat_rate,
                label: item.label,
                type: item.type,
                purchase_item_ids: item.purchase_item_ids // Pass specific IDs for used products
            }));

            // 1. Create Draft
            // Send adjustments to BFF
            const res = await axios.post('/api/pos/invoice', {
                items,
                adjustments // { kind, label, amount }
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.id) {
                const invId = res.data.id;
                setCurrentInvoiceId(invId);

                // 2. Validate
                await axios.post(`/api/pos/invoice/${invId}/validate`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // 3. Setup for Payment
                setInvoiceDetails(prev => ({
                    ...res.data,
                    adjustments: adjustments, // Inject adjustments for print
                    total_ttc: totalTTC
                }));

                setPaymentAmount(totalTTC);
                setCheckoutMode(true); // Switch Drawer view to Payment
            }
        } catch (error) {
            message.error(error.response?.data?.error || 'Checkout initiation failed');
        } finally {
            setLoading(false);
        }
    };

    const processPayment = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            // Execute Pay
            await axios.post('/api/pos/payment', {
                invoice_id: currentInvoiceId,
                amount: paymentAmount,
                payment_mode_id: paymentMethod
            }, { headers: { Authorization: `Bearer ${token}` } });

            message.success('Transaction Successful');

            // Optimistic UX: Locally decrement stock for immediate feedback
            optimisticDecrement(cart);

            // Clean up
            setCart([]);
            setAdjustments([]); // Clear adjustments
            setCheckoutMode(false); // Reset drawer mode
            setCartDrawerVisible(false); // Close Drawer

            // Trigger Auto-Print
            setAutoPrint(true);
            setPrintVisible(true);

            // Re-fetch from server (source of truth)
            fetchProducts();
        } catch (error) {
            message.error('Payment failed');
        } finally {
            setLoading(false);
        }
    };

    // Close print modal handler
    const handlePrintClose = () => {
        setPrintVisible(false);
        setAutoPrint(false); // Reset
    };

    // Columns
    const productColumns = [
        { title: 'Ref', dataIndex: 'ref', key: 'ref', width: 100, render: (text) => <Text type="secondary" style={{ fontSize: '12px' }}>{text}</Text> },
        {
            title: 'Label',
            dataIndex: 'label',
            key: 'label',
            render: (text, record) => (
                <div style={{ padding: '4px 0' }}>
                    <Text strong style={{ display: 'block', marginBottom: '2px' }}>{text}</Text>
                    <CategoryTag category_id={record.category_id} />
                </div>
            )
        },
        {
            title: 'Price (TTC)',
            dataIndex: 'price',
            key: 'price',
            align: 'right',
            width: 120,
            render: (val) => <Text strong style={{ color: '#52c41a' }}>{formatCurrency(val)}</Text>
        },
        {
            title: 'Stock',
            dataIndex: 'stock',
            key: 'stock',
            align: 'right',
            width: 80,
            render: (val, record) => (
                record.type === 1 ? <Tag>Service</Tag> :
                    <Tag color={val > 0 ? 'blue' : 'red'} style={{ minWidth: '35px', textAlign: 'center' }}>{val}</Tag>
            )
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            width: 80,
            render: (_, record) => (
                <Tooltip title={record.stock <= 0 && record.type === 0 ? 'Out of Stock' : 'Add'}>
                    <Button
                        type="primary"
                        icon={<ShoppingCartOutlined style={{ fontSize: '16px' }} />}
                        disabled={record.stock <= 0 && record.type === 0}
                        onClick={(e) => { e.stopPropagation(); addToCart(record); }}
                        style={{ height: '36px', width: '36px' }}
                    />
                </Tooltip>
            )
        }
    ];

    // Styles for Cashier Mode
    const containerStyle = cashierMode ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        background: 'transparent',
        padding: '16px',
        overflow: 'hidden'
    } : {
        padding: '24px',
        background: 'transparent',
        minHeight: 'calc(100vh - 64px)'
    };

    return (
        <div style={containerStyle}>
            <Row gutter={24} style={{ height: '100%' }}>
                <Col span={24} style={{ height: '100%' }}>
                    <Card
                        className="premium-card"
                        title={
                            <Space size="middle" wrap>
                                <div>
                                    <div className="section-title">Terminal</div>
                                    <Title level={4} style={{ margin: 0 }}>
                                        {cashierMode ? 'Cashier' : 'Catalog'}
                                    </Title>
                                </div>
                                <Divider type="vertical" />
                                <ProductCategorySelect
                                    id="pos-category-filter"
                                    style={{ width: cashierMode ? 200 : 150 }}
                                    value={selectedCategory === 'all' ? undefined : selectedCategory}
                                    onChange={val => setSelectedCategory(val || 'all')}
                                    placeholder="Categories"
                                />
                                <Input
                                    id="pos-search-input"
                                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                    placeholder="Search ( / )"
                                    style={{ width: cashierMode ? 240 : 180 }}
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    allowClear
                                />
                                <Divider type="vertical" />
                                <Radio.Group
                                    value={viewMode}
                                    onChange={e => {
                                        setViewMode(e.target.value);
                                        localStorage.setItem('pos_view_mode', e.target.value);
                                    }}
                                    size="small"
                                    buttonStyle="solid"
                                >
                                    <Radio.Button value="list"><UnorderedListOutlined /></Radio.Button>
                                    <Radio.Button value="grid"><AppstoreOutlined /></Radio.Button>
                                </Radio.Group>
                                {viewMode === 'grid' && (
                                    <Select
                                        size="small"
                                        value={gridDensity}
                                        onChange={v => {
                                            setGridDensity(v);
                                            localStorage.setItem('pos_grid_density', v);
                                        }}
                                        style={{ width: 100 }}
                                    >
                                        <Option value={2}>2 per row</Option>
                                        <Option value={3}>3 per row</Option>
                                    </Select>
                                )}
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => fetchProducts()}
                                    title="Refresh stock from Dolibarr"
                                    loading={storeLoading}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    type={cashierMode ? 'primary' : 'default'}
                                    icon={<FullscreenOutlined />}
                                    onClick={toggleCashierMode}
                                    title="Toggle Cashier Mode (F)"
                                >
                                    {cashierMode ? 'Exit' : 'Cashier'}
                                </Button>
                            </Space>
                        }
                        extra={null}
                        bodyStyle={{
                            height: cashierMode ? 'calc(100vh - 80px)' : 'calc(100vh - 200px)',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '16px'
                        }}
                    >
                        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text type="secondary">Found {filteredProducts.length} items</Text>
                            <Space>
                                {viewMode === 'grid' && <Text type="secondary" style={{ fontSize: '12px' }}>Mode: <b>{viewMode.toUpperCase()}</b></Text>}
                            </Space>
                        </div>

                        {viewMode === 'list' ? (
                            <Table
                                dataSource={filteredProducts}
                                columns={productColumns}
                                rowKey="id"
                                size="middle"
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    size: 'small',
                                    onChange: (page) => setCurrentPage(page)
                                }}
                                loading={storeLoading}
                                onRow={(record) => ({
                                    onClick: () => addToCart(record),
                                    style: { cursor: record.stock > 0 || record.type === 1 ? 'pointer' : 'not-allowed' }
                                })}
                                scroll={{ y: 'calc(100vh - 280px)' }}
                            />
                        ) : (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
                                    <Row gutter={[12, 12]}>
                                        {filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize).map(product => (
                                            <Col key={product.id} xs={24} sm={12} md={24 / gridDensity}>
                                                <ProductGridCard product={product} onAdd={addToCart} />
                                            </Col>
                                        ))}
                                        {filteredProducts.length === 0 && (
                                            <Col span={24}>
                                                <EmptyState
                                                    message="No products found"
                                                    description="Try a different search term or category to find what you're looking for."
                                                    icon={<AppstoreOutlined />}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                </div>
                                <div style={{ textAlign: 'right', marginTop: 'auto' }}>
                                    <Pagination
                                        current={currentPage}
                                        pageSize={pageSize}
                                        total={filteredProducts.length}
                                        onChange={(page) => setCurrentPage(page)}
                                        size="small"
                                        showSizeChanger={false}
                                    />
                                </div>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Floating Cart Button (Rule A: Hide when drawer open) */}
            {!cartDrawerVisible && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: 32,
                        right: 32,
                        zIndex: 10000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        borderRadius: '28px',
                        transition: 'opacity 0.3s',
                    }}
                >
                    <Button
                        type="primary"
                        shape="round"
                        size="large"
                        onClick={() => setCartDrawerVisible(true)}
                        style={{ height: '56px', padding: '0 24px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <Badge count={cart.reduce((a, c) => a + c.qty, 0)} showZero color="white" style={{ background: 'white', color: '#1890ff', fontWeight: 'bold' }} />
                        <span style={{ marginLeft: 8 }}>{formatCurrency(totalTTC)}</span>
                        <ShoppingCartOutlined style={{ fontSize: '20px' }} />
                    </Button>
                </div>
            )}

            {/* Cart Drawer */}
            <CartDrawer
                visible={cartDrawerVisible}
                onClose={() => {
                    setCartDrawerVisible(false);
                    setCheckoutMode(false); // Reset mode when closed
                }}
                cart={cart}
                onUpdateQty={updateQty}
                onRemoveItem={removeItem}
                onCheckout={handleCheckout}
                loading={loading}
                adjustments={adjustments}
                onAddAdjustment={addAdjustment}
                onRemoveAdjustment={removeAdjustment}
                // Check props
                checkoutMode={checkoutMode}
                onBackToCart={() => setCheckoutMode(false)}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                onFinish={processPayment}
            />

            {/* Auto Print Modal */}
            <InvoicePrintModal
                visible={printVisible}
                invoiceId={currentInvoiceId}
                onCancel={handlePrintClose}
                paymentMethod={paymentMethod}
                autoPrint={autoPrint}
            />

        </div>
    );
};

export default POS;
